import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useModal } from "hooks/useModal";
import { useAuth } from "auth";
import { useStripeAccount } from "features/stripe/useStripeAccount";
import { consumeOnboardingReturn } from "features/stripe/onboardingReturn";
import {
  resolveSettlementRoute,
  BLOCKED_ACCOUNT_MESSAGE,
  UNKNOWN_STATUS_MESSAGE,
  OnboardingVariant,
  SettlementRoute,
} from "features/stripe/settlementRouting";
import { useColumnCounterparty } from "features/column/useColumnCounterparty";
import { merchantIdSelector } from "store/userSliceHelpers";
import { SettlementAccountStatus } from "types/stripe";
import { SettlementPlatform } from "types/money-settlement";
import {
  ColumnCounterpartyFormData,
  CreateColumnCounterpartyInput,
} from "types/column";
import { apiErrorMessage } from "utils/errors/parseApiError";

const PLATFORM_LABELS: Record<SettlementPlatform, string> = {
  stripe: "Stripe",
  column: "Column",
};

const PENDING_REVIEW_MESSAGE =
  "The information you provided is still being reviewed by our payment " +
  "provider. There's nothing you need to do — your payment account will be " +
  "ready as soon as the review completes.";

// "Payment account" action for the settings menu. Same set-up path as the redeem
// CTA (features/balance-cta) MINUS the redeem step — it only ensures the merchant
// has a payout account and reports the outcome:
//  - account ready           → info message naming the platform
//  - Stripe, no account      → create it, then open hosted onboarding (Stripe portal)
//  - Column, no account      → collect bank details; success = account created
//  - anything else           → the same onboarding / review / blocked modals the
//                              redeem CTA shows, via the shared route resolver
// The account is keyed off the authenticated session server-side, so the
// merchantId passed to checkAccount is only a cache/label hint.
export function usePaymentAccount() {
  const merchantId = useSelector(merchantIdSelector) ?? "";
  const { user, initializing } = useAuth();
  const { checkAccount, createAccount } = useStripeAccount();
  const { createCounterparty, creating: columnSubmitting } =
    useColumnCounterparty();

  const {
    showModal: showColumnForm,
    openModal: openColumnFormModal,
    closeModal: closeColumnFormModal,
  } = useModal();
  const {
    showModal: showStripeOnboarding,
    openModal: openStripeOnboardingModal,
    closeModal: closeStripeOnboardingModal,
  } = useModal();
  const {
    showModal: showPendingReview,
    openModal: openPendingReviewModal,
    closeModal: closePendingReviewModal,
  } = useModal();
  const {
    showModal: showLoading,
    openModal: openLoadingModal,
    closeModal: closeLoadingModal,
  } = useModal();
  const {
    showModal: showSuccess,
    openModal: openSuccessModal,
    closeModal: closeSuccessModal,
  } = useModal();
  const {
    showModal: showError,
    openModal: openErrorModal,
    closeModal: closeErrorModal,
  } = useModal();

  const [loadingMsg, setLoadingMsg] = useState("Processing…");
  const [onboardingUrl, setOnboardingUrl] = useState("");
  const [onboardingVariant, setOnboardingVariant] =
    useState<OnboardingVariant>("required");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  function reportError(err: any, fallback: string) {
    setErrorMsg(apiErrorMessage(err, fallback));
    openErrorModal();
  }

  function showMessageError(message: string) {
    setErrorMsg(message);
    openErrorModal();
  }

  // Open exactly one modal for an already-resolved route. Same routes as the
  // redeem CTA; only "ready" differs, since there's nothing to redeem from here.
  function applyRoute(route: SettlementRoute, platformId: SettlementPlatform) {
    switch (route.kind) {
      case "ready":
        setSuccessMsg(
          `Your ${PLATFORM_LABELS[platformId]} payment account is ready.`,
        );
        openSuccessModal();
        return;
      case "column_form":
        openColumnFormModal();
        return;
      case "onboarding":
        setOnboardingUrl(route.url);
        setOnboardingVariant(route.variant);
        openStripeOnboardingModal();
        return;
      case "pending_review":
        openPendingReviewModal();
        return;
      case "blocked":
        showMessageError(BLOCKED_ACCOUNT_MESSAGE);
        return;
      // "create_stripe_account" is consumed by routeStatus below, so reaching it
      // here means a just-created account still reported no account.
      case "create_stripe_account":
      case "unknown":
      default:
        showMessageError(UNKNOWN_STATUS_MESSAGE);
        return;
    }
  }

  // Resolve a status to a route and act on it, creating the Stripe account first
  // if there isn't one yet. The caller owns the loading modal — it's already open
  // on every path that gets here.
  async function routeStatus(status: SettlementAccountStatus) {
    let route = resolveSettlementRoute(status);
    let platformId = status.platformId;

    if (route.kind === "create_stripe_account") {
      setLoadingMsg("Creating payout account…");
      const created = await createAccount(merchantId);
      route = resolveSettlementRoute(created);
      platformId = created.platformId;
      // A freshly created account cannot need creating again. Bail to the error
      // path rather than looping on a status we evidently can't interpret.
      if (route.kind === "create_stripe_account") route = { kind: "unknown" };
    }

    applyRoute(route, platformId);
  }

  // Same-tab return from Stripe-hosted onboarding that STARTED HERE. The redeem
  // CTA has its own handler (features/balance-cta) and would otherwise drop the
  // merchant into a redeem modal they never asked for; consumeOnboardingReturn
  // hands the return to exactly one of the two.
  //
  // This hook lives in MerchantNav, which mounts outside ProtectedRoute — and
  // merchantId comes from redux-persist, so it's set even when signed out. Wait
  // for the session before calling the API, or the check races auth and 401s.
  useEffect(() => {
    if (initializing || !user) return;
    if (!consumeOnboardingReturn("settings")) return;

    (async () => {
      setLoadingMsg("Checking payout account…");
      openLoadingModal();
      try {
        const status = await checkAccount(merchantId);
        // Didn't finish (or the link expired mid-way)? checkAccount mints a
        // fresh link, so the onboarding route offers to resume rather than
        // dead-ending here.
        await routeStatus(status);
      } catch (err: any) {
        reportError(err, "Failed to check payout account. Please try again.");
      } finally {
        closeLoadingModal();
      }
    })();
  }, [user, initializing]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleManagePaymentAccount() {
    setLoadingMsg("Checking payout account…");
    openLoadingModal();
    try {
      const status = await checkAccount(merchantId);
      await routeStatus(status);
    } catch (err: any) {
      reportError(err, "Failed to check payout account. Please try again.");
    } finally {
      closeLoadingModal();
    }
  }

  // Column bank-details submit. Unlike the redeem CTA, success just confirms the
  // account was created — no redeem modal.
  async function handleColumnSubmit(formData: ColumnCounterpartyFormData) {
    closeColumnFormModal();
    setLoadingMsg("Saving bank account…");
    openLoadingModal();

    const input: CreateColumnCounterpartyInput = { ...formData, country: "US" };

    try {
      await createCounterparty({ merchantId, input });
      closeLoadingModal();
      setSuccessMsg("Your payment account has been created successfully.");
      openSuccessModal();
    } catch (err: any) {
      closeLoadingModal();
      reportError(err, "Failed to save bank account. Please try again.");
    }
  }

  return {
    handleManagePaymentAccount,
    handleColumnSubmit,
    columnSubmitting,
    onboardingUrl,
    onboardingVariant,
    loadingMsg,
    errorMsg,
    successMsg,
    pendingReviewMsg: PENDING_REVIEW_MESSAGE,
    showColumnForm,
    closeColumnForm: closeColumnFormModal,
    showStripeOnboarding,
    closeStripeOnboardingModal,
    showPendingReview,
    closePendingReviewModal,
    showLoading,
    showSuccess,
    closeSuccessModal,
    showError,
    closeErrorModal,
  };
}
